import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from '../context/AuthContext';
import usePageTitle from "../hooks/usePageTitle";
import {
  fetchVersions,
  createVersion as createVersionRequest,
  updateVersion as updateVersionRequest,
  patchVersionStatus,
} from "../services/versions";

const semverPattern = /^\d+\.\d+\.\d+$/;

const defaultForm = {
  id: "",
  versionNumber: "",
  platform: "windows",
  arch: "x64",
  channel: "stable",
  status: "active",
  isLatest: false,
  isMandatory: false,
  forceFrom: "",
  minimumRequiredVersion: "",
  downloadUrl: "",
  releaseNotes: "",
  checksum: "",
  fileSize: 0,
  publishedAt: "",
};

const selectOptions = {
  platform: ["windows", "macos", "linux"],
  arch: ["x64", "arm64"],
  channel: ["stable", "beta", "internal"],
  status: ["active", "inactive"],
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("es-ES");
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoUtc = (value) => (value ? new Date(value).toISOString() : "");

const getEffectiveVersionLabel = (version) => {
  if (version.status !== "active") {
    return "No publicada";
  }

  if (version.isMandatory && version.minimumRequiredVersion) {
    return `Obligatoria desde ${version.minimumRequiredVersion}`;
  }

  if (version.isLatest) {
    return "Última versión activa";
  }

  return "Disponible para su canal";
};

const validateForm = (form) => {
  const errors = {};

  if (!semverPattern.test(form.versionNumber.trim())) {
    errors.versionNumber = "La versión debe tener formato semver x.y.z";
  }

  if (form.minimumRequiredVersion && !semverPattern.test(form.minimumRequiredVersion.trim())) {
    errors.minimumRequiredVersion =
      "La versión mínima requerida debe tener formato semver x.y.z";
  }

  if (form.isMandatory && !form.forceFrom) {
    errors.forceFrom = "forceFrom es obligatorio cuando la actualización es obligatoria";
  }

  if (form.fileSize !== "" && Number.isNaN(Number(form.fileSize))) {
    errors.fileSize = "El tamaño debe ser numérico";
  }

  return errors;
};

const buildPayload = (form) => ({
  versionNumber: form.versionNumber.trim(),
  platform: form.platform,
  arch: form.arch,
  channel: form.channel,
  status: form.status,
  isLatest: form.isLatest,
  isMandatory: form.isMandatory,
  forceFrom: form.forceFrom ? toIsoUtc(form.forceFrom) : null,
  minimumRequiredVersion: form.minimumRequiredVersion.trim() || null,
  downloadUrl: form.downloadUrl.trim() || null,
  releaseNotes: form.releaseNotes.trim(),
  checksum: form.checksum.trim() || null,
  fileSize: Number(form.fileSize || 0),
  publishedAt: form.publishedAt ? toIsoUtc(form.publishedAt) : null,
});

const AppVersionsPageModern = () => {
  usePageTitle("Versiones de la aplicación");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});

  const versionsQuery = useQuery({
    queryKey: ["app-versions"],
    queryFn: () => fetchVersions(navigate),
    enabled: isAdmin,
  });

  const refreshVersions = () =>
    queryClient.invalidateQueries({
      queryKey: ["app-versions"],
    });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editingId
        ? updateVersionRequest(editingId, payload, navigate)
        : createVersionRequest(payload, navigate),
    onSuccess: async () => {
      await refreshVersions();
      setDialogOpen(false);
      setEditingId(null);
      setForm(defaultForm);
      setFormErrors({});
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => patchVersionStatus(id, status, navigate),
    onSuccess: refreshVersions,
  });

  const versions = versionsQuery.data?.versions || [];

  const openCreateDialog = () => {
    setEditingId(null);
    setForm(defaultForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (version) => {
    setEditingId(version.id);
    setForm({
      id: version.id,
      versionNumber: version.versionNumber || "",
      platform: version.platform || "windows",
      arch: version.arch || "x64",
      channel: version.channel || "stable",
      status: version.status || "active",
      isLatest: Boolean(version.isLatest),
      isMandatory: Boolean(version.isMandatory),
      forceFrom: toInputDateTime(version.forceFrom),
      minimumRequiredVersion: version.minimumRequiredVersion || "",
      downloadUrl: version.downloadUrl || "",
      releaseNotes: version.releaseNotes || "",
      checksum: version.checksum || "",
      fileSize: version.fileSize || 0,
      publishedAt: toInputDateTime(version.publishedAt),
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setFormErrors((current) => ({
      ...current,
      [field]: undefined,
    }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(form);
    setFormErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      await saveMutation.mutateAsync(buildPayload(form));
    } catch (_error) {
      // El error ya se muestra a través del estado de la mutación.
    }
  };

  if (!isAdmin) {
    return (
      <Box className="wp-page" component="section" sx={{ flex: 1, p: 4, bgcolor: "background.default", color: "text.primary" }}>
        <Alert severity="error">
          Acceso denegado. Solo los administradores pueden acceder a esta sección.
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="wp-page" component="section" sx={{ flex: 1, p: 4, bgcolor: "background.default", color: "text.primary" }}>
      <Stack
        className="wp-heading"
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography component="h1" variant="h4" gutterBottom>
            Versiones
          </Typography>
          <Typography color="text.secondary">
            Gestiona las versiones publicadas para el cliente Qt y consulta qué recibirán los usuarios.
          </Typography>
        </Box>

        <Button variant="contained" onClick={openCreateDialog}>
          Nueva versión
        </Button>
      </Stack>

      {versionsQuery.isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {versionsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {versionsQuery.error?.message || "Error al cargar las versiones"}
        </Alert>
      )}

      {!versionsQuery.isLoading && !versionsQuery.isError && versions.length === 0 && (
        <Alert severity="info">No hay versiones publicadas. Crea la primera versión.</Alert>
      )}

      {!versionsQuery.isLoading && !versionsQuery.isError && versions.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Versión</TableCell>
                <TableCell>Plataforma</TableCell>
                <TableCell>Arquitectura</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Última</TableCell>
                <TableCell>Obligatoria</TableCell>
                <TableCell>forceFrom</TableCell>
                <TableCell>URL descarga</TableCell>
                <TableCell>Publicada</TableCell>
                <TableCell>Versión efectiva</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell>{version.versionNumber}</TableCell>
                  <TableCell>{version.platform}</TableCell>
                  <TableCell>{version.arch}</TableCell>
                  <TableCell>{version.channel}</TableCell>
                  <TableCell>{version.status}</TableCell>
                  <TableCell>{version.isLatest ? "Sí" : "No"}</TableCell>
                  <TableCell>{version.isMandatory ? "Sí" : "No"}</TableCell>
                  <TableCell>{formatDate(version.forceFrom)}</TableCell>
                  <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
                    {version.downloadUrl || "—"}
                  </TableCell>
                  <TableCell>{formatDate(version.publishedAt)}</TableCell>
                  <TableCell>{getEffectiveVersionLabel(version)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => openEditDialog(version)}>
                        Editar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          statusMutation.mutate({
                            id: version.id,
                            status: version.status === "active" ? "inactive" : "active",
                          })
                        }
                      >
                        {version.status === "active" ? "Desactivar" : "Activar"}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Editar versión" : "Nueva versión"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {saveMutation.isError && (
              <Alert severity="error">{saveMutation.error?.message || "Error al guardar"}</Alert>
            )}

            <TextField
              label="versionNumber"
              value={form.versionNumber}
              onChange={(event) => handleFieldChange("versionNumber", event.target.value)}
              error={Boolean(formErrors.versionNumber)}
              helperText={formErrors.versionNumber}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {["platform", "arch", "channel", "status"].map((field) => (
                <FormControl key={field} fullWidth>
                  <InputLabel>{field}</InputLabel>
                  <Select
                    label={field}
                    value={form[field]}
                    onChange={(event) => handleFieldChange(field, event.target.value)}
                  >
                    {selectOptions[field].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isLatest}
                    onChange={(event) => handleFieldChange("isLatest", event.target.checked)}
                  />
                }
                label="isLatest"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isMandatory}
                    onChange={(event) => handleFieldChange("isMandatory", event.target.checked)}
                  />
                }
                label="isMandatory"
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="forceFrom"
                type="datetime-local"
                value={form.forceFrom}
                onChange={(event) => handleFieldChange("forceFrom", event.target.value)}
                error={Boolean(formErrors.forceFrom)}
                helperText={formErrors.forceFrom}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="minimumRequiredVersion"
                value={form.minimumRequiredVersion}
                onChange={(event) =>
                  handleFieldChange("minimumRequiredVersion", event.target.value)
                }
                error={Boolean(formErrors.minimumRequiredVersion)}
                helperText={formErrors.minimumRequiredVersion}
                fullWidth
              />
            </Stack>

            <TextField
              label="downloadUrl"
              value={form.downloadUrl}
              onChange={(event) => handleFieldChange("downloadUrl", event.target.value)}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="checksum"
                value={form.checksum}
                onChange={(event) => handleFieldChange("checksum", event.target.value)}
                fullWidth
              />
              <TextField
                label="fileSize"
                type="number"
                value={form.fileSize}
                onChange={(event) => handleFieldChange("fileSize", event.target.value)}
                error={Boolean(formErrors.fileSize)}
                helperText={formErrors.fileSize}
                fullWidth
              />
            </Stack>

            <TextField
              label="publishedAt"
              type="datetime-local"
              value={form.publishedAt}
              onChange={(event) => handleFieldChange("publishedAt", event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="releaseNotes"
              value={form.releaseNotes}
              onChange={(event) => handleFieldChange("releaseNotes", event.target.value)}
              multiline
              minRows={4}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saveMutation.isPending}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppVersionsPageModern;
